import type { ProjectTask, TaskGroup, TimberProject } from '../types';
import {
  familyFromGroupName,
  formatGroupTimelineSectionLabel,
  listGroupTimelineFamilyParentTasks,
  parseGroupTimelineProjectSectionId,
  type GroupTimelineFamily,
  type GroupTimelineProjectLabel,
} from './groupTimeline';
import {
  orgNameFromMap,
  parseSupplierGroupId,
  UNASSIGNED_SUPPLIER_KEY,
  MULTIPLE_SUPPLIER_KEY,
  type GroupTimelineSupplierIndex,
  type GroupTimelineSupplierResolution,
} from './groupTimelineSupplier';
import { differenceInCalendarDays } from 'date-fns';
import { rollupDatesFromChildren } from './subtaskDateRollup';
import { getSubtaskStructures } from './subtaskStructures';
import { stripTime } from './taskSchedule';

export const GROUP_TIMELINE_CONFLICT_FAMILIES = [
  'procurement',
  'production',
  'installation',
] as const;

export type GroupTimelineConflictFamily = (typeof GROUP_TIMELINE_CONFLICT_FAMILIES)[number];

export const INSTALL_CONFLICT_BUCKET = '__install_crew__';

export type GroupTimelineConflictSide = {
  projectId: string;
  projectLabel: string;
  taskId: string;
  taskTitle: string;
  groupId?: string;
  startDate: Date;
  endDate: Date;
  /** Rolled-up planned start of the parent bar (Install / Cut Timber Package). */
  plannedStartDate?: Date;
  /** Parent and/or subtasks that actually carry this pair’s supplier. */
  supplierTaskIds: string[];
};

export type GroupTimelineConflictPair = {
  family: GroupTimelineConflictFamily;
  supplierOrgId: string | null;
  supplierName: string;
  a: GroupTimelineConflictSide;
  b: GroupTimelineConflictSide;
  overlapStart: Date;
  overlapEnd: Date;
  overlapDays: number;
};

export type GroupTimelineMissingSupplier = {
  family: 'procurement' | 'production';
  projectId: string;
  projectLabel: string;
  taskId: string;
  taskTitle: string;
  groupId?: string;
  startDate?: Date;
  /** Structures on this package with no approved quote and no supplier. */
  uncoveredStructureNames?: string[];
  /** Parent/child task ids that still need a supplier (badge follows these when expanded). */
  uncoveredTaskIds?: string[];
};

export type GroupTimelineConflictIssues = {
  pairs: GroupTimelineConflictPair[];
  missing: GroupTimelineMissingSupplier[];
};

export type GroupTimelineConflictReport = GroupTimelineConflictIssues & {
  family: GroupTimelineConflictFamily;
};

export type GroupTimelineConflictBoard = Record<
  GroupTimelineConflictFamily,
  GroupTimelineConflictReport
>;

/** Parent titled Install — not “Installation Complete”. */
export function isInstallConflictTitle(title: string | undefined): boolean {
  const n = (title || '').trim().toLowerCase();
  if (!n) return false;
  if (n === 'install') return true;
  return /^install(?:\s|[—–\-:·])/.test(n);
}

/** The only parents that can drive a family conflict / missing-supplier badge. */
export function isGroupTimelineConflictTitle(
  title: string | undefined,
  family: GroupTimelineConflictFamily,
): boolean {
  const n = (title || '').trim().toLowerCase();
  if (!n) return false;
  if (family === 'procurement') return n.includes('timber lead time');
  if (family === 'production') return n.includes('cut timber package');
  return isInstallConflictTitle(title);
}

/** Cut Timber Package / Timber Lead Time — parent or child can carry a supplier. */
export function isGroupTimelineSupplierAssignableTitle(title: string | undefined): boolean {
  return isGroupTimelineConflictTitle(title, 'production')
    || isGroupTimelineConflictTitle(title, 'procurement');
}

export function supplierCapabilityForTaskTitle(
  title: string | undefined,
): 'cnc' | 'timber' | null {
  if (isGroupTimelineConflictTitle(title, 'production')) return 'cnc';
  if (isGroupTimelineConflictTitle(title, 'procurement')) return 'timber';
  return null;
}

export function isGroupTimelineConflictFamily(
  value: string | null | undefined,
): value is GroupTimelineConflictFamily {
  return !!value && (GROUP_TIMELINE_CONFLICT_FAMILIES as readonly string[]).includes(value);
}

export function hasConflictIssues(issues: GroupTimelineConflictIssues | null | undefined): boolean {
  if (!issues) return false;
  return issues.pairs.length > 0 || issues.missing.length > 0;
}

export type ConflictBadgeTone = 'danger' | 'warning';

/** Production / Install badges go red when the planned start is this close. */
export const CONFLICT_BADGE_URGENT_WITHIN_DAYS = 30;

/**
 * Planned start is urgent when it is today, already past, or within the next
 * {@link CONFLICT_BADGE_URGENT_WITHIN_DAYS} local calendar days (inclusive).
 */
export function isConflictStartUrgent(start: Date, now: Date = new Date()): boolean {
  return differenceInCalendarDays(stripTime(start), stripTime(now)) <= CONFLICT_BADGE_URGENT_WITHIN_DAYS;
}

function sidePlannedStart(side: GroupTimelineConflictSide): Date {
  return side.plannedStartDate ?? side.startDate;
}

export type ConflictBadgeToneScope = {
  /** Only this project’s planned starts (Gantt project / group row). */
  projectId?: string;
  /** Only this parent or carrier task’s planned start (Gantt task row). */
  taskId?: string;
};

function pairStartsForScope(
  pair: GroupTimelineConflictPair,
  scope?: ConflictBadgeToneScope,
): Date[] {
  if (scope?.taskId) {
    if (pair.a.taskId === scope.taskId) return [sidePlannedStart(pair.a)];
    if (pair.b.taskId === scope.taskId) return [sidePlannedStart(pair.b)];
    if ((pair.a.supplierTaskIds ?? []).includes(scope.taskId)) return [pair.a.startDate];
    if ((pair.b.supplierTaskIds ?? []).includes(scope.taskId)) return [pair.b.startDate];
    return [];
  }
  if (scope?.projectId) {
    const dates: Date[] = [];
    if (pair.a.projectId === scope.projectId) dates.push(sidePlannedStart(pair.a));
    if (pair.b.projectId === scope.projectId) dates.push(sidePlannedStart(pair.b));
    return dates;
  }
  return [sidePlannedStart(pair.a), sidePlannedStart(pair.b)];
}

function productionInstallStarts(
  issues: GroupTimelineConflictIssues,
  scope?: ConflictBadgeToneScope,
): Date[] {
  const dates: Date[] = [];
  for (const pair of issues.pairs) {
    if (pair.family !== 'production' && pair.family !== 'installation') continue;
    dates.push(...pairStartsForScope(pair, scope));
  }
  return dates;
}

/**
 * Missing supplier is always red. Procurement overlaps stay yellow.
 * Production / Install overlaps are red when any planned start is within
 * 30 calendar days, otherwise yellow caution.
 */
export function conflictBadgeTone(
  issues: GroupTimelineConflictIssues,
  now: Date = new Date(),
  scope?: ConflictBadgeToneScope,
): ConflictBadgeTone {
  const missingInScope = issues.missing.filter((miss) => {
    if (scope?.projectId && miss.projectId !== scope.projectId) return false;
    if (scope?.taskId && miss.taskId !== scope.taskId) return false;
    return true;
  });
  if (missingInScope.length > 0) return 'danger';

  const prodInstallStarts = productionInstallStarts(issues, scope);
  if (prodInstallStarts.length > 0) {
    return prodInstallStarts.some((start) => isConflictStartUrgent(start, now))
      ? 'danger'
      : 'warning';
  }

  return 'warning';
}

function dayMs(d: Date): number {
  return stripTime(new Date(d)).getTime();
}

function inclusiveOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): { start: Date; end: Date; days: number } | null {
  const as = dayMs(aStart);
  const ae = dayMs(aEnd);
  const bs = dayMs(bStart);
  const be = dayMs(bEnd);
  if (as > ae || bs > be) return null;
  if (as > be || bs > ae) return null;
  const start = Math.max(as, bs);
  const end = Math.min(ae, be);
  return {
    start: new Date(start),
    end: new Date(end),
    days: Math.round((end - start) / 86400000) + 1,
  };
}

function projectLabel(
  projectId: string,
  projectById: Map<string, TimberProject>,
  projectLabels?: Map<string, GroupTimelineProjectLabel>,
): string {
  const project = projectById.get(projectId);
  const label = projectLabels?.get(projectId);
  return formatGroupTimelineSectionLabel({
    projectNumber: label?.projectNumber,
    descriptor: label?.descriptor,
    projectName: project?.name,
  });
}

function childrenOf(parentId: string, tasks: ProjectTask[]): ProjectTask[] {
  return tasks.filter((t) => t.parentTaskId === parentId);
}

/** Every subtask under a scoped parent (Cut Timber Package, Timber Lead Time, …). */
function descendantsOf(parentId: string, tasks: ProjectTask[]): ProjectTask[] {
  const out: ProjectTask[] = [];
  const walk = (pid: string) => {
    for (const kid of childrenOf(pid, tasks)) {
      out.push(kid);
      if (kid.id) walk(kid.id);
    }
  };
  walk(parentId);
  return out;
}

function isOpenTask(task: ProjectTask): boolean {
  return task.status !== 'completed';
}

function taskOwnWindow(task: ProjectTask): { startDate: Date; endDate: Date } | null {
  return rollupDatesFromChildren([task]);
}

function scopedWindow(parent: ProjectTask, allTasks: ProjectTask[]): { startDate: Date; endDate: Date } | null {
  const kids = parent.id ? childrenOf(parent.id, allTasks) : [];
  // Same window the Gantt summary bar uses: children only when the parent has subtasks.
  if (kids.length > 0) return rollupDatesFromChildren(kids);
  return taskOwnWindow(parent);
}

/** Scheduled start for Timber Lead Time / Cut Timber Package parent bars (Gantt rollup). */
export function supplierFocusTaskStartDate(
  parent: ProjectTask,
  allTasks: ProjectTask[],
): Date | null {
  return scopedWindow(parent, allTasks)?.startDate ?? null;
}

type ConflictCarrier = {
  taskId: string;
  startDate: Date;
  endDate: Date;
};

function carriersFromTasks(tasks: ProjectTask[]): ConflictCarrier[] {
  const out: ConflictCarrier[] = [];
  for (const task of tasks) {
    if (!task.id || !isOpenTask(task)) continue;
    const window = taskOwnWindow(task);
    if (!window) continue;
    out.push({ taskId: task.id, ...window });
  }
  return out;
}

/**
 * When a parent has subtasks, each child window is a carrier. The rolled-up
 * parent bar is not used for overlap — a gap between children is not a conflict.
 */
function conflictCarriers(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  orgId: string,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): ConflictCarrier[] {
  const kids = parent.id ? childrenOf(parent.id, allTasks) : [];
  if (kids.length > 0) {
    if (orgId === INSTALL_CONFLICT_BUCKET) return carriersFromTasks(kids);
    const kidCarriers = kids.filter((t) => taskHasOrg(t, orgId, byTaskId));
    if (kidCarriers.length) return carriersFromTasks(kidCarriers);
    return carriersFromTasks(kids);
  }
  if (orgId !== INSTALL_CONFLICT_BUCKET && !taskHasOrg(parent, orgId, byTaskId)) {
    return [];
  }
  return carriersFromTasks([parent]);
}

function firstCarrierOverlap(
  left: ConflictCarrier[],
  right: ConflictCarrier[],
): {
  overlap: { start: Date; end: Date; days: number };
  left: ConflictCarrier;
  right: ConflictCarrier;
  leftIds: string[];
  rightIds: string[];
} | null {
  let best: {
    overlap: { start: Date; end: Date; days: number };
    left: ConflictCarrier;
    right: ConflictCarrier;
  } | null = null;
  const leftIds = new Set<string>();
  const rightIds = new Set<string>();
  for (const a of left) {
    for (const b of right) {
      const overlap = inclusiveOverlap(a.startDate, a.endDate, b.startDate, b.endDate);
      if (!overlap) continue;
      leftIds.add(a.taskId);
      rightIds.add(b.taskId);
      if (!best || overlap.days > best.overlap.days) best = { overlap, left: a, right: b };
    }
  }
  if (!best) return null;
  return {
    ...best,
    leftIds: Array.from(leftIds),
    rightIds: Array.from(rightIds),
  };
}

function isTrackedOrgId(id: string | undefined): id is string {
  return !!id && id !== UNASSIGNED_SUPPLIER_KEY && id !== MULTIPLE_SUPPLIER_KEY;
}

function orgIdsOnTask(
  task: ProjectTask,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] {
  const ids = new Set<string>();
  for (const id of task.supplierOrgIds ?? []) {
    if (isTrackedOrgId(id)) ids.add(id);
  }
  const res = task.id && byTaskId ? byTaskId.get(task.id) : undefined;
  for (const id of res?.orgIds ?? []) {
    if (isTrackedOrgId(id)) ids.add(id);
  }
  return Array.from(ids);
}

function taskHasOrg(
  task: ProjectTask,
  orgId: string,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): boolean {
  return orgIdsOnTask(task, byTaskId).includes(orgId);
}

/** Subtasks that carry this org; otherwise the parent when it is the only carrier. */
export function carrierTasksForOrg(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  orgId: string,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): ProjectTask[] {
  const kids = parent.id ? childrenOf(parent.id, allTasks) : [];
  const kidCarriers = kids.filter((t) => taskHasOrg(t, orgId, byTaskId));
  if (kidCarriers.length) return kidCarriers;
  if (taskHasOrg(parent, orgId, byTaskId)) return [parent];
  return [];
}

export function supplierOrgIdsForScopedTask(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] {
  const ids = new Set<string>();
  const consider = parent.id
    ? [parent, ...descendantsOf(parent.id, allTasks)]
    : [parent];
  for (const task of consider) {
    for (const id of orgIdsOnTask(task, byTaskId)) ids.add(id);
  }
  return Array.from(ids);
}

export type ProposalStructureRef = { id: string; name: string };

function normStructureName(name: string | undefined): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Proposal structures that are not on a subtask of this package.
 * Empty unless at least one proposal structure is already a subtask and another is not.
 */
export function unassignedSubtaskStructureNames(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  proposalStructures: ProposalStructureRef[] | undefined,
): string[] {
  const proposal = (proposalStructures ?? []).filter((s) => (s.id || s.name || '').trim());
  if (proposal.length < 2) return [];
  const kids = parent.id ? descendantsOf(parent.id, allTasks) : [];
  const assignedIds = new Set<string>();
  const assignedNames = new Set<string>();
  for (const kid of kids) {
    for (const structure of getSubtaskStructures(kid)) {
      if (structure.id) assignedIds.add(structure.id);
      const name = normStructureName(structure.name);
      if (name) assignedNames.add(name);
    }
  }
  const missing: string[] = [];
  const seen = new Set<string>();
  let assignedCount = 0;
  for (const structure of proposal) {
    const name = normStructureName(structure.name);
    const hit = (!!structure.id && assignedIds.has(structure.id)) || (!!name && assignedNames.has(name));
    if (hit) {
      assignedCount += 1;
      continue;
    }
    const label = (structure.name || structure.id).trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    missing.push(label);
  }
  if (assignedCount === 0 || missing.length === 0) return [];
  return missing;
}

function mergeStructureNames(left: string[] | null | undefined, right: string[]): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const raw of [...(left ?? []), ...right]) {
    const label = raw.trim();
    const key = normStructureName(label);
    if (!label || !key || seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }
  return names;
}

/**
 * Structure names on this package that have no supplier.
 * `null` when the package has no structures (caller uses the whole-package rule).
 */
export function uncoveredStructureNamesForScopedTask(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] | null {
  const descendants = parent.id ? descendantsOf(parent.id, allTasks) : [];
  const consider = parent.id ? [parent, ...descendants] : [parent];
  const names: string[] = [];
  const seen = new Set<string>();
  let anyStructure = false;
  for (const task of consider) {
    const structs = getSubtaskStructures(task);
    if (!structs.length) continue;
    anyStructure = true;
    if (orgIdsOnTask(task, byTaskId).length > 0) continue;
    for (const structure of structs) {
      const label = (structure.name || structure.id).trim();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      names.push(label);
    }
  }
  if (!anyStructure) return null;
  return names;
}

/** Tasks on this package that still need a supplier (parent and/or children). */
export function uncoveredTaskIdsForScopedTask(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] {
  const directKids = parent.id ? childrenOf(parent.id, allTasks) : [];
  const descendants = parent.id ? descendantsOf(parent.id, allTasks) : [];
  const consider = parent.id ? [parent, ...descendants] : [parent];
  const withStructures: string[] = [];
  let anyStructure = false;
  for (const task of consider) {
    if (!task.id) continue;
    if (getSubtaskStructures(task).length === 0) continue;
    anyStructure = true;
    if (orgIdsOnTask(task, byTaskId).length === 0) withStructures.push(task.id);
  }
  if (anyStructure) return withStructures;
  if (directKids.length > 0) {
    return descendants
      .filter((task) => !!task.id && orgIdsOnTask(task, byTaskId).length === 0)
      .map((task) => task.id!);
  }
  return parent.id ? [parent.id] : [];
}

function isScopedParent(task: ProjectTask, family: GroupTimelineConflictFamily): boolean {
  return isGroupTimelineConflictTitle(task.title, family);
}

function toSide(
  task: ProjectTask,
  window: { startDate: Date; endDate: Date },
  projectById: Map<string, TimberProject>,
  projectLabels: Map<string, GroupTimelineProjectLabel> | undefined,
  supplierTaskIds: string[],
  plannedStartDate?: Date,
): GroupTimelineConflictSide {
  return {
    projectId: task.projectId,
    projectLabel: projectLabel(task.projectId, projectById, projectLabels),
    taskId: task.id || '',
    taskTitle: (task.title || '').trim() || 'Untitled',
    groupId: task.groupId,
    startDate: window.startDate,
    endDate: window.endDate,
    plannedStartDate: plannedStartDate ?? window.startDate,
    supplierTaskIds,
  };
}

export function buildGroupTimelineConflictReport(params: {
  family: GroupTimelineConflictFamily;
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  supplierIndex?: GroupTimelineSupplierIndex | null;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  /** Enabled proposal structures keyed by deal id. */
  proposalStructuresByDealId?: Map<string, ProposalStructureRef[]>;
}): GroupTimelineConflictReport {
  const {
    family,
    projects,
    groups,
    tasks,
    supplierIndex,
    projectLabels,
    proposalStructuresByDealId,
  } = params;
  const projectById = new Map(projects.filter((p) => p.id).map((p) => [p.id!, p]));
  const parents = listGroupTimelineFamilyParentTasks({
    family,
    projects,
    groups,
    tasks,
  }).filter((t) => isOpenTask(t) && isScopedParent(t, family) && !!t.id);

  const missing: GroupTimelineMissingSupplier[] = [];
  const assigned: Array<{
    task: ProjectTask;
    carriers: ConflictCarrier[];
    orgIds: string[];
  }> = [];

  for (const parent of parents) {
    if (family === 'installation') {
      const carriers = conflictCarriers(parent, tasks, INSTALL_CONFLICT_BUCKET);
      if (!carriers.length) continue;
      assigned.push({
        task: parent,
        carriers,
        orgIds: [INSTALL_CONFLICT_BUCKET],
      });
      continue;
    }
    const orgIds = supplierOrgIdsForScopedTask(parent, tasks, supplierIndex?.byTaskId);
    const uncovered = uncoveredStructureNamesForScopedTask(parent, tasks, supplierIndex?.byTaskId);
    const dealId = projectById.get(parent.projectId)?.dealId?.trim() || '';
    const unassigned = unassignedSubtaskStructureNames(
      parent,
      tasks,
      dealId ? proposalStructuresByDealId?.get(dealId) : undefined,
    );
    const structureNames = mergeStructureNames(uncovered, unassigned);
    const packageMissing = structureNames.length > 0
      || (uncovered === null && unassigned.length === 0 && orgIds.length === 0);
    if (packageMissing) {
      const window = scopedWindow(parent, tasks);
      if (window || structureNames.length > 0) {
        const uncoveredTaskIds = uncoveredTaskIdsForScopedTask(parent, tasks, supplierIndex?.byTaskId);
        if (unassigned.length > 0 && parent.id && !uncoveredTaskIds.includes(parent.id)) {
          uncoveredTaskIds.push(parent.id);
        }
        missing.push({
          family,
          projectId: parent.projectId,
          projectLabel: projectLabel(parent.projectId, projectById, projectLabels),
          taskId: parent.id!,
          taskTitle: (parent.title || '').trim() || 'Untitled',
          groupId: parent.groupId,
          startDate: window?.startDate,
          uncoveredStructureNames: structureNames.length > 0 ? structureNames : undefined,
          uncoveredTaskIds,
        });
      }
      if (orgIds.length === 0) continue;
    }
    for (const orgId of orgIds) {
      const carriers = conflictCarriers(parent, tasks, orgId, supplierIndex?.byTaskId);
      if (!carriers.length) continue;
      assigned.push({
        task: parent,
        carriers,
        orgIds: [orgId],
      });
    }
  }

  const byBucket = new Map<string, typeof assigned>();
  for (const row of assigned) {
    for (const orgId of row.orgIds) {
      const list = byBucket.get(orgId) ?? [];
      list.push(row);
      byBucket.set(orgId, list);
    }
  }

  const pairs: GroupTimelineConflictPair[] = [];
  const seen = new Set<string>();
  byBucket.forEach((rows, orgId) => {
    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        const left = rows[i];
        const right = rows[j];
        if (left.task.projectId === right.task.projectId) continue;
        const hit = firstCarrierOverlap(left.carriers, right.carriers);
        if (!hit) continue;
        const idA = left.task.id!;
        const idB = right.task.id!;
        const pairKey = [orgId, idA, idB].sort().join('|');
        if (seen.has(pairKey)) continue;
        seen.add(pairKey);
        const aFirst = (left.task.projectId || '').localeCompare(right.task.projectId || '') <= 0;
        const first = aFirst ? left : right;
        const second = aFirst ? right : left;
        const firstHit = aFirst ? hit.left : hit.right;
        const secondHit = aFirst ? hit.right : hit.left;
        const firstIds = aFirst ? hit.leftIds : hit.rightIds;
        const secondIds = aFirst ? hit.rightIds : hit.leftIds;
        const firstPlanned = scopedWindow(first.task, tasks)?.startDate;
        const secondPlanned = scopedWindow(second.task, tasks)?.startDate;
        pairs.push({
          family,
          supplierOrgId: orgId === INSTALL_CONFLICT_BUCKET ? null : orgId,
          supplierName: orgId === INSTALL_CONFLICT_BUCKET
            ? 'Install crew'
            : orgNameFromMap(orgId, supplierIndex?.orgNameById ?? new Map()),
          a: toSide(first.task, firstHit, projectById, projectLabels, firstIds, firstPlanned),
          b: toSide(second.task, secondHit, projectById, projectLabels, secondIds, secondPlanned),
          overlapStart: hit.overlap.start,
          overlapEnd: hit.overlap.end,
          overlapDays: hit.overlap.days,
        });
      }
    }
  });

  pairs.sort((x, y) => {
    const byName = x.a.projectLabel.localeCompare(y.a.projectLabel);
    if (byName !== 0) return byName;
    return x.b.projectLabel.localeCompare(y.b.projectLabel);
  });
  missing.sort((x, y) => x.projectLabel.localeCompare(y.projectLabel) || x.taskTitle.localeCompare(y.taskTitle));

  return { family, pairs, missing };
}

export function emptyConflictBoard(): GroupTimelineConflictBoard {
  return {
    procurement: { family: 'procurement', pairs: [], missing: [] },
    production: { family: 'production', pairs: [], missing: [] },
    installation: { family: 'installation', pairs: [], missing: [] },
  };
}

export function buildGroupTimelineConflictBoard(params: {
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  procurementSuppliers?: GroupTimelineSupplierIndex | null;
  productionSuppliers?: GroupTimelineSupplierIndex | null;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  proposalStructuresByDealId?: Map<string, ProposalStructureRef[]>;
}): GroupTimelineConflictBoard {
  return {
    procurement: buildGroupTimelineConflictReport({
      family: 'procurement',
      projects: params.projects,
      groups: params.groups,
      tasks: params.tasks,
      supplierIndex: params.procurementSuppliers,
      projectLabels: params.projectLabels,
      proposalStructuresByDealId: params.proposalStructuresByDealId,
    }),
    production: buildGroupTimelineConflictReport({
      family: 'production',
      projects: params.projects,
      groups: params.groups,
      tasks: params.tasks,
      supplierIndex: params.productionSuppliers,
      projectLabels: params.projectLabels,
      proposalStructuresByDealId: params.proposalStructuresByDealId,
    }),
    installation: buildGroupTimelineConflictReport({
      family: 'installation',
      projects: params.projects,
      groups: params.groups,
      tasks: params.tasks,
      projectLabels: params.projectLabels,
    }),
  };
}

export function mergeConflictIssues(list: GroupTimelineConflictIssues[]): GroupTimelineConflictIssues {
  const pairKeys = new Set<string>();
  const pairs: GroupTimelineConflictPair[] = [];
  const missingKeys = new Set<string>();
  const missing: GroupTimelineMissingSupplier[] = [];
  for (const item of list) {
    for (const pair of item.pairs) {
      const key = [pair.family, pair.supplierOrgId || '', pair.a.taskId, pair.b.taskId].join('|');
      if (pairKeys.has(key)) continue;
      pairKeys.add(key);
      pairs.push(pair);
    }
    for (const miss of item.missing) {
      const key = `${miss.family}:${miss.taskId}`;
      if (missingKeys.has(key)) continue;
      missingKeys.add(key);
      missing.push(miss);
    }
  }
  return { pairs, missing };
}

export function issuesForFamilyChip(
  board: GroupTimelineConflictBoard,
  family: GroupTimelineFamily,
): GroupTimelineConflictIssues {
  if (family === 'all') {
    return mergeConflictIssues(GROUP_TIMELINE_CONFLICT_FAMILIES.map((f) => board[f]));
  }
  if (isGroupTimelineConflictFamily(family)) return board[family];
  return { pairs: [], missing: [] };
}

export function issuesForProjectIds(
  board: GroupTimelineConflictBoard,
  projectIds: ReadonlySet<string>,
  family: GroupTimelineFamily,
): GroupTimelineConflictIssues {
  const reports = family === 'all'
    ? GROUP_TIMELINE_CONFLICT_FAMILIES.map((f) => board[f])
    : isGroupTimelineConflictFamily(family)
      ? [board[family]]
      : [];
  const filtered = reports.map((report) => ({
    pairs: report.pairs.filter((p) => projectIds.has(p.a.projectId) || projectIds.has(p.b.projectId)),
    missing: report.missing.filter((m) => projectIds.has(m.projectId)),
  }));
  return mergeConflictIssues(filtered);
}

export function issuesForSupplierOrg(
  board: GroupTimelineConflictBoard,
  orgId: string,
  family: GroupTimelineFamily,
): GroupTimelineConflictIssues {
  const reports = family === 'all'
    ? GROUP_TIMELINE_CONFLICT_FAMILIES.map((f) => board[f])
    : isGroupTimelineConflictFamily(family)
      ? [board[family]]
      : [];
  const filtered = reports.map((report) => ({
    pairs: report.pairs.filter((p) => p.supplierOrgId === orgId),
    missing: [] as GroupTimelineMissingSupplier[],
  }));
  return mergeConflictIssues(filtered);
}

export type GroupTimelineGanttRowLookup = {
  sectionId?: string | null;
  groupId?: string | null;
  groupName?: string | null;
  groupProjectId?: string | null;
};

function emptyIssues(): GroupTimelineConflictIssues {
  return { pairs: [], missing: [] };
}

function reportsForView(
  board: GroupTimelineConflictBoard,
  family: GroupTimelineFamily,
): GroupTimelineConflictReport[] {
  if (family === 'all') return GROUP_TIMELINE_CONFLICT_FAMILIES.map((f) => board[f]);
  if (isGroupTimelineConflictFamily(family)) return [board[family]];
  return [];
}

/** Issues that belong on a Gantt left section / group / supplier row. */
export function issuesForGanttRow(
  board: GroupTimelineConflictBoard,
  viewFamily: GroupTimelineFamily,
  row: GroupTimelineGanttRowLookup,
): GroupTimelineConflictIssues {
  if (row.sectionId) {
    const projectId = parseGroupTimelineProjectSectionId(row.sectionId);
    if (projectId) return issuesForProjectIds(board, new Set([projectId]), viewFamily);
    if (isGroupTimelineConflictFamily(row.sectionId)) {
      if (viewFamily !== 'all' && viewFamily !== row.sectionId) return emptyIssues();
      return board[row.sectionId];
    }
  }

  if (row.groupId) {
    const supplierKey = parseSupplierGroupId(row.groupId);
    if (supplierKey) {
      if (supplierKey === UNASSIGNED_SUPPLIER_KEY) {
        return mergeConflictIssues(
          reportsForView(board, viewFamily).map((report) => ({
            pairs: [],
            missing: report.missing,
          })),
        );
      }
      return issuesForSupplierOrg(board, supplierKey, viewFamily);
    }
    const syntheticProjectId = parseGroupTimelineProjectSectionId(row.groupId);
    if (syntheticProjectId) {
      return issuesForProjectIds(board, new Set([syntheticProjectId]), viewFamily);
    }
  }

  if (row.groupProjectId) {
    const issues = issuesForProjectIds(board, new Set([row.groupProjectId]), viewFamily);
    if (!row.groupName) return issues;
    const inferred = familyFromGroupName(row.groupName);
    if (inferred && isGroupTimelineConflictFamily(inferred)) {
      return {
        pairs: issues.pairs.filter((p) => p.family === inferred),
        missing: issues.missing.filter((m) => m.family === inferred),
      };
    }
    // Design / Preconstruction / etc. never carry supplier or install conflicts.
    return emptyIssues();
  }

  return emptyIssues();
}

function pairMentionsTask(pair: GroupTimelineConflictPair, taskId: string): boolean {
  return pair.a.taskId === taskId || pair.b.taskId === taskId;
}

function pairMentionsSupplierTask(pair: GroupTimelineConflictPair, taskId: string): boolean {
  return (pair.a.supplierTaskIds ?? []).includes(taskId)
    || (pair.b.supplierTaskIds ?? []).includes(taskId);
}

function sideForParent(pair: GroupTimelineConflictPair, parentTaskId: string): GroupTimelineConflictSide | null {
  if (pair.a.taskId === parentTaskId) return pair.a;
  if (pair.b.taskId === parentTaskId) return pair.b;
  return null;
}

/** Issues that belong on a visible scoped parent task (Install, Cut Timber Package, …). */
export function issuesForTaskId(
  board: GroupTimelineConflictBoard,
  viewFamily: GroupTimelineFamily,
  taskId: string | undefined,
): GroupTimelineConflictIssues {
  if (!taskId) return emptyIssues();
  return mergeConflictIssues(
    reportsForView(board, viewFamily).map((report) => ({
      pairs: report.pairs.filter((p) => pairMentionsTask(p, taskId)),
      missing: report.missing.filter((m) => m.taskId === taskId),
    })),
  );
}

/**
 * Badge placement on a Gantt task/subtask row.
 * Collapsed parent keeps every pair and missing-supplier warning. Expanded parent
 * keeps only pairs/missing that still belong on the parent; a subtask shows its
 * own supplier overlap and any missing-supplier warning that follows it.
 */
export function issuesForGanttTaskRow(
  board: GroupTimelineConflictBoard,
  viewFamily: GroupTimelineFamily,
  params: {
    taskId: string | undefined;
    parentTaskId?: string | null;
    parentExpanded?: boolean;
  },
): GroupTimelineConflictIssues {
  const taskId = params.taskId;
  if (!taskId) return emptyIssues();

  if (params.parentTaskId) {
    const parentIssues = issuesForTaskId(board, viewFamily, params.parentTaskId);
    const pairIssues: GroupTimelineConflictIssues[] = reportsForView(board, viewFamily).map((report) => ({
      pairs: report.pairs.filter((p) => pairMentionsSupplierTask(p, taskId)),
      missing: [],
    }));
    return mergeConflictIssues([
      ...pairIssues,
      {
        pairs: [],
        missing: parentIssues.missing.filter((m) => missingFollowsToTask(m, taskId)),
      },
    ]);
  }

  const parentIssues = issuesForTaskId(board, viewFamily, taskId);
  if (!params.parentExpanded) return parentIssues;
  return {
    pairs: parentIssues.pairs.filter((pair) => {
      const side = sideForParent(pair, taskId);
      if (!side) return false;
      return !(side.supplierTaskIds ?? []).some((id) => id !== taskId);
    }),
    missing: parentIssues.missing.filter((m) => missingStaysOnExpandedParent(m, taskId)),
  };
}

function missingFollowsToTask(
  miss: GroupTimelineMissingSupplier,
  taskId: string,
): boolean {
  const ids = miss.uncoveredTaskIds ?? [];
  if (ids.length > 0) return ids.includes(taskId);
  return miss.taskId === taskId;
}

function missingStaysOnExpandedParent(
  miss: GroupTimelineMissingSupplier,
  parentTaskId: string,
): boolean {
  const ids = miss.uncoveredTaskIds ?? [];
  if (ids.length === 0) return miss.taskId === parentTaskId;
  return ids.includes(parentTaskId);
}
